import React, { useEffect, useState } from 'react';
import { useUserData } from '../../hooks/useUserData';
import { useContracts } from '../../hooks/useContract';
import { useNotify } from '../../context/NotificationContext';
import { formatUsdc, bnToNumber } from '../../utils/format';
import { RANK_NAMES, NUM_RANKS, NUM_MILESTONES } from '../../utils/constants';
import { NetworkGuard } from '../wallet/NetworkGuard';
import { InlineLoader, ButtonSpinner } from '../common/Loader';
import { Alert } from '../common/StatBox';
import { parseError } from '../../utils/errors';

const RANK_ICONS = ['🥈', '🥇', '💠', '💎', '👑'];

export const Ranks = () => (
  <NetworkGuard>
    <RanksInner />
  </NetworkGuard>
);

const RanksInner = () => {
  const { userId, isRegistered, basic, extended, refresh } = useUserData();
  const { viewsR, logicW } = useContracts();
  const notify = useNotify();

  const [reqs, setReqs] = useState(null);
  const [claimable, setClaimable] = useState(null);
  const [milestoneReqs, setMilestoneReqs] = useState(null);
  const [milestoneStatus, setMilestoneStatus] = useState(null);
  const [claiming, setClaiming] = useState(null);   // rank index or 'all'
  const [promoting, setPromoting] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!viewsR) return;
      try {
        // Rank requirements
        const reqPromises = [];
        for (let i = 0; i < NUM_RANKS; i++) reqPromises.push(viewsR.getRankRequirement(i));
        const rArr = await Promise.all(reqPromises);
        if (cancelled) return;
        setReqs(rArr);

        // Milestone requirements
        const msPromises = [];
        for (let i = 0; i < NUM_MILESTONES; i++) msPromises.push(viewsR.getMilestoneRequirement(i));
        const msArr = await Promise.all(msPromises);
        if (cancelled) return;
        setMilestoneReqs(msArr);

        if (userId) {
          const [cl, ms] = await Promise.all([
            viewsR.getUserAllClaimableRankPools(userId).catch(() => null),
            viewsR.getUserMilestoneStatus(userId).catch(() => null)
          ]);
          if (cancelled) return;
          setClaimable(cl);
          setMilestoneStatus(ms);
        }
      } catch (e) {}
    };
    load();
    return () => { cancelled = true; };
  }, [viewsR, userId, reload]);

  if (!isRegistered) return <Alert type="info">Register and invest to unlock ranks and milestones.</Alert>;
  if (!reqs || !milestoneReqs) return <InlineLoader />;

  const currentRank = extended?.rank || 0;

  const claimOne = async (rankIdx) => {
    if (!logicW) return;
    setClaiming(rankIdx);
    try {
      const tx = await logicW.claimRankPool(rankIdx);
      notify.info('Claiming rank pool...');
      await tx.wait();
      notify.success(`${RANK_NAMES[rankIdx + 1]} pool claimed!`);
      refresh();
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally { setClaiming(null); }
  };

  const claimAll = async () => {
    if (!logicW) return;
    setClaiming('all');
    try {
      const tx = await logicW.claimAllRankPools();
      notify.info('Claiming all rank pools...');
      await tx.wait();
      notify.success('All rank pools claimed!');
      refresh();
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally { setClaiming(null); }
  };

  const checkPromotion = async () => {
    if (!logicW) return;
    setPromoting(true);
    try {
      const tx = await logicW.checkAndPromoteRank(userId);
      notify.info('Checking rank...');
      await tx.wait();
      notify.success('Rank check complete');
      refresh();
      setReload((r) => r + 1);
    } catch (e) {
      notify.error(parseError(e));
    } finally { setPromoting(false); }
  };

  return (
    <div>
      {/* Header stats */}
      <div className="user-card">
        <div className="user-card-top">
          <div className="user-avatar-ring">
            <svg viewBox="0 0 68 68"><circle cx="34" cy="34" r="31" fill="none" stroke="#ffb800" strokeWidth="2" strokeDasharray="6 4" /></svg>
            <div className="inner-avatar" style={{ background: 'var(--grad-gold)' }}>🏆</div>
          </div>
          <div className="user-card-info">
            <div className="user-card-id">{RANK_NAMES[currentRank] || 'Unranked'}</div>
            <div className="user-card-wallet">Your current rank</div>
          </div>
        </div>
        <div className="flex" style={{ gap: 8 }}>
          <button className="btn btn-secondary btn-sm" onClick={checkPromotion} disabled={promoting}>
            {promoting && <ButtonSpinner />}Check Promotion
          </button>
          {claimable && !claimable.total.isZero() && (
            <button className="btn btn-gold btn-sm" onClick={claimAll} disabled={claiming === 'all'}>
              {claiming === 'all' && <ButtonSpinner />}Claim All ${formatUsdc(claimable.total)}
            </button>
          )}
        </div>
      </div>

      {/* RANKS */}
      <div className="section-card">
        <div className="section-card-title">
          <span><span className="icon">🏆</span> Rank Progress</span>
        </div>

        {reqs.map((req, idx) => {
          const rankNum = idx + 1;
          const achieved = currentRank >= rankNum;
          const userClaimable = claimable ? claimable.amounts[idx] : null;

          // Progress calc
          const selfPct = basic && !req.selfInvestment.isZero()
            ? Math.min(100, Number(basic.totalInvestment.mul(100).div(req.selfInvestment)))
            : (achieved ? 100 : 0);
          const dirPct = extended && bnToNumber(req.directs) > 0
            ? Math.min(100, (bnToNumber(extended.activeDirects) / bnToNumber(req.directs)) * 100)
            : (achieved ? 100 : 0);
          const volPct = extended && !req.teamVolume.isZero()
            ? Math.min(100, Number(extended.teamVolume.mul(100).div(req.teamVolume)))
            : (achieved ? 100 : 0);
          const overall = achieved ? 100 : Math.min(selfPct, dirPct, volPct);

          const selfMet = basic && basic.totalInvestment.gte(req.selfInvestment);
          const dirMet  = extended && bnToNumber(extended.activeDirects) >= bnToNumber(req.directs);
          const volMet  = extended && extended.teamVolume.gte(req.teamVolume);

          return (
            <div key={idx} className={`rank-mini ${achieved ? 'achieved' : ''}`}>
              <div className="rank-mini-header">
                <div className="rank-emoji">{RANK_ICONS[idx]}</div>
                <div style={{ flex: 1 }}>
                  <div className="rank-mini-title">{RANK_NAMES[rankNum]}</div>
                  <div className="rank-mini-earned">
                    {achieved ? '✓ Achieved' : `${overall.toFixed(0)}% complete`}
                  </div>
                </div>
                {achieved && <span className="badge badge-gold">Active</span>}
              </div>

              <div>
                <div className="rank-req-row">
                  <span className="k">Self Investment</span>
                  <span className={`v ${selfMet ? 'met' : 'unmet'}`}>${formatUsdc(req.selfInvestment)}</span>
                </div>
                <div className="rank-req-row">
                  <span className="k">Active Directs</span>
                  <span className={`v ${dirMet ? 'met' : 'unmet'}`}>{bnToNumber(req.directs)}</span>
                </div>
                <div className="rank-req-row">
                  <span className="k">Team Volume</span>
                  <span className={`v ${volMet ? 'met' : 'unmet'}`}>${formatUsdc(req.teamVolume)}</span>
                </div>
                {bnToNumber(req.requiredLegCount) > 0 && (
                  <div className="rank-req-row">
                    <span className="k">Legs Required</span>
                    <span className="v">{bnToNumber(req.requiredLegCount)}× {RANK_NAMES[req.requiredLegRank]}</span>
                  </div>
                )}
              </div>

              <div className="rank-progress-bar">
                <div className="rank-progress-fill" style={{ width: `${overall}%` }} />
              </div>

              {achieved && userClaimable && !userClaimable.isZero() && (
                <div className="rank-earned-strip">
                  <span className="lbl">💎 You've earned</span>
                  <div className="flex" style={{ gap: 8, alignItems: 'center' }}>
                    <span className="amt">${formatUsdc(userClaimable)}</span>
                    <button className="btn btn-primary btn-sm" onClick={() => claimOne(idx)} disabled={claiming === idx}>
                      {claiming === idx && <ButtonSpinner />}
                      Claim
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* MILESTONES */}
      <div className="section-card">
        <div className="section-card-title">
          <span><span className="icon">⭐</span> Leadership Milestones</span>
        </div>
        {milestoneReqs.map((r, i) => {
          const eligible = milestoneStatus ? milestoneStatus.eligible[i] : false;
          const rewarded = milestoneStatus ? milestoneStatus.rewarded[i] : false;
          const cls = rewarded ? 'rewarded' : eligible ? 'eligible' : '';
          const members = bnToNumber(r.activeMembers);
          const curMembers = extended ? bnToNumber(extended.activeMembersDownline) : 0;

          return (
            <div key={i} className={`milestone-mini ${cls}`}>
              <div className="milestone-num-mini">{i + 1}</div>
              <div className="milestone-mini-info">
                <div className="milestone-mini-title">Milestone #{i + 1}</div>
                <div className="milestone-mini-progress">
                  {curMembers}/{members} members · ${formatUsdc(extended?.teamVolume)}/${formatUsdc(r.teamVolume)}
                </div>
              </div>
              {rewarded && <span className="badge badge-gold">✓ Rewarded</span>}
              {!rewarded && eligible && <span className="badge badge-green">Eligible</span>}
              {!rewarded && !eligible && <span className="badge badge-muted">In Progress</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};
